// Indian Payroll Calculations
// PF: 12% of Basic (employee + employer). Employee PF may be capped at ₹15,000 when applyPfCeilingToEmployee is true.
// ESI: 0.75% of Gross (if gross <= ESIC threshold; default 21000)
// Professional Tax: State-wise, simplified to 200/month
// TDS: As per Indian tax slabs (New Regime 2025-26)

export const EMPLOYER_PF_BASIC_CAP = 15000;
export const DEFAULT_ESIC_THRESHOLD = 21000;

export function isEmployeePfCeilingApplied(salaryOrFlag) {
  if (typeof salaryOrFlag === 'boolean') return salaryOrFlag;
  return salaryOrFlag?.applyPfCeilingToEmployee === true;
}

export function employeePfWageBase(basic, applyPfCeilingToEmployee = false) {
  const wage = Number(basic) || 0;
  if (!isEmployeePfCeilingApplied(applyPfCeilingToEmployee)) return wage;
  return Math.min(wage, EMPLOYER_PF_BASIC_CAP);
}

export function calculatePF(basic, applyPfCeilingToEmployee = false) {
  return Math.round(employeePfWageBase(basic, applyPfCeilingToEmployee) * 0.12);
}

export function employeePfLabel(applyPfCeilingToEmployee = false) {
  return isEmployeePfCeilingApplied(applyPfCeilingToEmployee)
    ? `Employee Provident Fund (12% of Basic, capped at ₹${EMPLOYER_PF_BASIC_CAP.toLocaleString('en-IN')})`
    : 'Employee Provident Fund (12% of Basic)';
}

/** `null` / empty = cap disabled (fall through). `0` and positive numbers are valid caps. */
export function getExplicitLimit(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return null;
  return n;
}

export function isLimitEnabled(value) {
  return getExplicitLimit(value) != null;
}

/** @deprecated Use getExplicitLimit — 0 is now a valid enabled cap. */
export function getPositiveLimit(value) {
  return getExplicitLimit(value);
}

export function resolveWageLimit(value, fallback) {
  return getExplicitLimit(value) ?? fallback;
}

export function resolveEmployerPfLimit(pfLimit) {
  return resolveWageLimit(pfLimit, EMPLOYER_PF_BASIC_CAP);
}

export function resolveEsicThreshold(threshold) {
  return resolveWageLimit(threshold, DEFAULT_ESIC_THRESHOLD);
}

export function getProjectDisplayName(project) {
  if (!project || typeof project !== 'object') return '';
  const name = String(project.name || project.projectName || '').trim();
  const code = String(project.code || project.projectCode || '').trim();
  if (name && code && name !== code) return `${name} (${code})`;
  return name || code || String(project.projectId || project.id || '').trim();
}

const findAssignedProjectWithCap = (assignedProjectIds = [], projects = [], field) => {
  const ids = Array.isArray(assignedProjectIds) ? assignedProjectIds : [];
  for (const id of ids) {
    const match = (projects || []).find((p) => p?.projectId === id || p?.id === id || p?.name === id);
    if (!match) continue;
    const limit = getExplicitLimit(match?.[field] ?? match?.salary?.[field]);
    if (limit != null) return match;
  }
  return null;
};

const lookupProject = (projectRef, projects = []) => {
  if (!projectRef) return null;
  if (typeof projectRef === 'object') {
    const id = projectRef.projectId || projectRef.id || projectRef.code;
    const fromList = id
      ? (projects || []).find((p) => p?.projectId === id || p?.id === id || p?.code === id || p?.name === id)
      : null;
    return fromList || projectRef;
  }
  return (projects || []).find(
    (p) => p?.projectId === projectRef || p?.id === projectRef || p?.code === projectRef || p?.name === projectRef
  ) || null;
};

/**
 * Employee override → effective (API) → nested salary.project → assigned project → system default.
 */
export function resolveSalaryWageCaps(salary, options = {}) {
  const sal = salary || {};
  const nestedProject = sal.project && typeof sal.project === 'object' ? sal.project : {};
  const assignedPfProject = findAssignedProjectWithCap(options.assignedProjectIds, options.projects, 'employerPfLimit');
  const assignedEsicProject = findAssignedProjectWithCap(options.assignedProjectIds, options.projects, 'esicThreshold');

  const employeePf = getExplicitLimit(sal.employerPfLimit);
  const projectPf =
    getExplicitLimit(sal.effectivePfLimit) ??
    getExplicitLimit(nestedProject.employerPfLimit) ??
    getExplicitLimit(assignedPfProject?.employerPfLimit ?? assignedPfProject?.salary?.employerPfLimit);
  const pfLimit = employeePf ?? projectPf ?? EMPLOYER_PF_BASIC_CAP;

  const employeeEsic = getExplicitLimit(sal.esicThreshold);
  const projectEsic =
    getExplicitLimit(sal.effectiveEsicThreshold) ??
    getExplicitLimit(nestedProject.esicThreshold) ??
    getExplicitLimit(assignedEsicProject?.esicThreshold ?? assignedEsicProject?.salary?.esicThreshold);
  const esicThreshold = employeeEsic ?? projectEsic ?? DEFAULT_ESIC_THRESHOLD;

  const namedLabel = (projectRef) => getProjectDisplayName(projectRef) || '';
  const projectLabelCandidate =
    namedLabel(lookupProject(nestedProject, options.projects)) ||
    namedLabel(assignedPfProject) ||
    namedLabel(assignedEsicProject) ||
    namedLabel(lookupProject((options.assignedProjectIds || [])[0], options.projects)) ||
    null;
  const pfSource = employeePf != null ? 'employee' : projectPf != null ? 'project' : 'system';
  const esicSource = employeeEsic != null ? 'employee' : projectEsic != null ? 'project' : 'system';
  const usesProjectCaps = pfSource === 'project' || esicSource === 'project';

  return {
    pfLimit,
    esicThreshold,
    pfSource,
    esicSource,
    projectLabel: usesProjectCaps ? projectLabelCandidate : null,
  };
}

export function formatWageCapSourceLabel(source, projectLabel) {
  if (source === 'employee') return 'employee override';
  if (source === 'project') return projectLabel ? `project: ${projectLabel}` : 'from project';
  return 'system default';
}

export function calculateEmployerPF(basic, pfLimit) {
  const cappedBasic = Math.min(Number(basic) || 0, resolveEmployerPfLimit(pfLimit));
  return Math.round(cappedBasic * 0.13);
}

export function calculateESI(gross, threshold) {
  if (gross <= resolveEsicThreshold(threshold)) {
    return Math.round(gross * 0.0075);
  }
  return 0;
}

export function calculateEmployerESI(gross, threshold) {
  if (gross <= resolveEsicThreshold(threshold)) {
    return Math.round(gross * 0.0325);
  }
  return 0;
}

export function calculateProfessionalTax() {
  return 200; // Simplified
}

export function calculateTDS(annualIncome) {
  // New Tax Regime 2025-26 slabs
  let tax = 0;
  const income = annualIncome;
  
  if (income <= 300000) {
    tax = 0;
  } else if (income <= 700000) {
    tax = (income - 300000) * 0.05;
  } else if (income <= 1000000) {
    tax = 20000 + (income - 700000) * 0.10;
  } else if (income <= 1200000) {
    tax = 50000 + (income - 1000000) * 0.15;
  } else if (income <= 1500000) {
    tax = 80000 + (income - 1200000) * 0.20;
  } else {
    tax = 140000 + (income - 1500000) * 0.30;
  }
  
  // 4% Health & Education Cess
  tax = tax + tax * 0.04;
  
  // Monthly TDS
  return Math.round(tax / 12);
}

export function calculateGross(salary) {
  const { basic = 0, hra = 0, specialAllowance = 0 } = salary;
  return basic + hra + specialAllowance;
}

export function calculateReimbursements(salary) {
  const { conveyance = 0 } = salary;
  return conveyance;
}

export function getCalendarDaysInMonth(month, year) {
  const m = Number(month);
  const y = Number(year);
  if (!Number.isFinite(m) || !Number.isFinite(y) || m < 1 || m > 12) return 0;
  return new Date(y, m, 0).getDate();
}

export function resolvePayrollDaysInMonth(employee, options = {}) {
  const fromCalendar = getCalendarDaysInMonth(options.month, options.year);
  if (fromCalendar > 0) return fromCalendar;
  const fromOptions = Number(options.daysInMonth);
  if (Number.isFinite(fromOptions) && fromOptions > 0) return fromOptions;
  const fromEmp = Number(employee?.daysInMonth);
  if (Number.isFinite(fromEmp) && fromEmp > 0) return fromEmp;
  return 0;
}

/**
 * Split in-hand across bank transfer + other. Always: byBank + other === inHand.
 * Default: full amount in byBank. Prefer sticky `other` when in-hand changes.
 */
export function resolvePaymentSplit(inHandSalary, overrides = {}, employee = {}) {
  const inHand = Math.max(Math.round(Number(inHandSalary) || 0), 0);

  if (overrides.other !== undefined) {
    const other = Math.min(Math.max(Number(overrides.other) || 0, 0), inHand);
    return { byBank: inHand - other, other };
  }

  if (overrides.byBank !== undefined) {
    const byBank = Math.min(Math.max(Number(overrides.byBank) || 0, 0), inHand);
    return { byBank, other: inHand - byBank };
  }

  if (employee?.other != null && employee?.other !== '') {
    const other = Math.min(Math.max(Number(employee.other) || 0, 0), inHand);
    return { byBank: inHand - other, other };
  }

  if (employee?.byBank != null && employee?.byBank !== '') {
    const byBank = Math.min(Math.max(Number(employee.byBank) || 0, 0), inHand);
    return { byBank, other: inHand - byBank };
  }

  return { byBank: inHand, other: 0 };
}

/**
 * Live payroll amounts using calendar days (not working days).
 * paidDays = daysInMonth - lopDays + bonusDays
 */
export function recalculatePayrollEmployee(employee, overrides = {}, options = {}) {
  const gross = Number(employee?.gross) || 0;
  const daysInMonth = resolvePayrollDaysInMonth(employee, options);
  const lopFromAttendance = Math.max(Number(employee?.lopFromAttendance) || 0, 0);

  const parsedManual = Number(overrides.manualLopAdd ?? employee?.manualLopAdd ?? 0);
  const rawManualLopAdd = Number.isFinite(parsedManual) ? Math.max(parsedManual, 0) : 0;
  const manualLopAdd = daysInMonth > 0 ? Math.min(rawManualLopAdd, daysInMonth) : rawManualLopAdd;
  const bonusDays = Math.max(Number(overrides.bonusDays ?? employee?.bonusDays ?? 0) || 0, 0);
  const advanceDeduction = Math.max(
    Number(overrides.advanceOverride ?? employee?.advanceDeduction ?? 0) || 0,
    0
  );

  const perDayRate = daysInMonth > 0 ? gross / daysInMonth : 0;
  const lopDays = lopFromAttendance + manualLopAdd;
  const paidDays = daysInMonth - lopDays + bonusDays;
  const lopDeduction = Math.round(perDayRate * lopDays);
  const dayBasedBonus = Math.round(perDayRate * bonusDays);
  const fixedMonthlyBonus = Math.max(Number(employee?.fixedMonthlyBonus) || 0, 0);
  const monthlyBonus = fixedMonthlyBonus + dayBasedBonus;

  const reimbursements = employee?.reimbursements ?? 0;
  const baseDeductionsExcludingAdvance = Math.max(
    (employee?.totalDeductions ?? 0) - (employee?.advanceDeduction ?? 0),
    0
  );
  const totalDeductions = baseDeductionsExcludingAdvance + advanceDeduction;
  const effectiveGross = gross - lopDeduction + monthlyBonus;
  const inHandSalary = Math.round(effectiveGross + reimbursements - totalDeductions);
  const ctc = Math.round(
    effectiveGross + reimbursements + ((employee?.bonus ?? 0) / 12) + (employee?.employerContribTotal ?? 0)
  );
  const { byBank, other } = resolvePaymentSplit(inHandSalary, overrides, employee);

  return {
    ...employee,
    daysInMonth,
    manualLopAdd,
    bonusDays,
    lopDays,
    paidDays,
    perDayRate,
    lopDeduction,
    monthlyBonus,
    effectiveGross,
    advanceDeduction,
    totalDeductions,
    inHandSalary,
    netPay: inHandSalary,
    byBank,
    other,
    ctc,
  };
}

export function calculateNetSalary(salary, lopDays = 0, daysInMonth = 30, bonus = 0) {
  const gross = calculateGross(salary);
  const reimbursements = calculateReimbursements(salary);
  const perDayPay = daysInMonth > 0 ? gross / daysInMonth : 0;
  const lopDeduction = Math.round(perDayPay * lopDays);
  const effectiveGross = gross - lopDeduction + bonus;
  const { pfLimit, esicThreshold } = resolveSalaryWageCaps(salary);

  const pf = calculatePF(salary.basic || 0, salary.applyPfCeilingToEmployee);
  const esi = calculateESI(effectiveGross, esicThreshold);
  const pt = calculateProfessionalTax();
  const tds = salary.tds || calculateTDS(effectiveGross * 12);
  
  const totalDeductions = pf + esi + pt + tds;
  const netPay = effectiveGross + reimbursements - totalDeductions;
  
  return {
    gross,
    reimbursements,
    effectiveGross,
    lopDays,
    lopDeduction,
    bonus,
    pf,
    employerPF: calculateEmployerPF(salary.basic || 0, pfLimit),
    esi,
    employerESI: calculateEmployerESI(effectiveGross, esicThreshold),
    gratuity: Math.round((salary.basic || 0) * 0.0481),
    pt,
    tds,
    totalDeductions,
    netPay: Math.round(netPay),
    basic: salary.basic || 0,
    hra: salary.hra || 0,
    specialAllowance: salary.specialAllowance || 0,
    conveyance: salary.conveyance || 0,
    telephoneExpense: salary.telephoneExpense || 0,
    mediclaim: salary.mediclaim || 0,
  };
}

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Monthly salary components plus the totals shown on the salary tab. */
export function summarizeSalaryStructure(salary = {}) {
  const basic = Math.round(Number(salary?.basic) || 0);
  const hra = Math.round(Number(salary?.hra) || 0);
  const specialAllowance = Math.round(Number(salary?.specialAllowance) || 0);
  const conveyance = Math.round(Number(salary?.conveyance) || 0);
  const telephoneExpense = Math.round(Number(salary?.telephoneExpense) || 0);
  const mediclaim = Math.round(Number(salary?.mediclaim) || 0);
  const tds = Math.round(Number(salary?.tds) || 0);
  const epf = Math.round(Number(salary?.epf) || 0);
  const esic = Math.round(Number(salary?.esic) || 0);
  const bonus = Math.round(Number(salary?.bonus) || 0);
  const employerPF = Math.round(Number(salary?.employerPF) || 0);
  const employerESIC = Math.round(Number(salary?.employerESIC) || 0);
  const gratuity = Math.round(Number(salary?.gratuity) || 0);
  const gross = basic + hra + specialAllowance;
  const totalReimb = conveyance;
  const totalDed = epf + esic + tds;
  const inHand = gross + totalReimb - totalDed;
  const employerTotal = employerPF + employerESIC + gratuity + mediclaim + telephoneExpense;
  const ctc = gross + totalReimb + bonus + employerTotal;
  return {
    basic,
    hra,
    specialAllowance,
    conveyance,
    telephoneExpense,
    mediclaim,
    tds,
    epf,
    esic,
    bonus,
    employerPF,
    employerESIC,
    gratuity,
    gross,
    totalReimb,
    totalDed,
    inHand,
    employerTotal,
    ctc,
  };
}

/** Positive rupee addition to basic. Percentage is of current basic. Returns null when it does not increase basic. */
export function applyBasicIncrement(basic, mode, value) {
  const current = Math.round(Number(basic) || 0);
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const addition = mode === 'amount' ? Math.round(amount) : Math.round((current * amount) / 100);
  if (addition <= 0) return null;
  return current + addition;
}

/**
 * Same derived fields the salary tab rewrites when basic changes:
 * HRA 40%, PF 12%, bonus 8.33%, employer PF 13% (capped), gratuity 4.81%, ESIC from the new gross.
 */
export function salaryAfterBasicChange(salary, nextBasic, wageCaps = {}) {
  const base = summarizeSalaryStructure(salary);
  const basic = Math.round(Number(nextBasic) || 0);
  const hra = Math.round(basic * 0.4);
  const gross = basic + hra + base.specialAllowance;
  const epf = calculatePF(basic, salary?.applyPfCeilingToEmployee);
  const esic = calculateESI(gross, wageCaps?.esicThreshold);
  const bonus = Math.round(basic * 0.0833);
  const employerPF = calculateEmployerPF(basic, wageCaps?.pfLimit);
  const employerESIC = calculateEmployerESI(gross, wageCaps?.esicThreshold);
  const gratuity = Math.round(basic * 0.0481);
  const totalDed = epf + esic + base.tds;
  const inHand = gross + base.totalReimb - totalDed;
  const employerTotal = employerPF + employerESIC + gratuity + base.mediclaim + base.telephoneExpense;
  const ctc = gross + base.totalReimb + bonus + employerTotal;
  return {
    ...base,
    basic,
    hra,
    gross,
    epf,
    esic,
    bonus,
    employerPF,
    employerESIC,
    gratuity,
    totalDed,
    inHand,
    employerTotal,
    ctc,
  };
}

export function buildIncrementSalaryRequest(nextSalary, meta = {}) {
  const next = nextSalary || {};
  return {
    salary: {
      basic: Math.round(Number(next.basic) || 0),
      hra: Math.round(Number(next.hra) || 0),
      specialAllowance: Math.round(Number(next.specialAllowance) || 0),
      conveyance: Math.round(Number(next.conveyance) || 0),
      telephoneExpense: Math.round(Number(next.telephoneExpense) || 0),
      tds: Math.round(Number(next.tds) || 0),
      mediclaim: Math.round(Number(next.mediclaim) || 0),
      epf: Math.round(Number(next.epf) || 0),
      esic: Math.round(Number(next.esic) || 0),
      bonus: Math.round(Number(next.bonus) || 0),
      employerPF: Math.round(Number(next.employerPF) || 0),
      employerESIC: Math.round(Number(next.employerESIC) || 0),
      gratuity: Math.round(Number(next.gratuity) || 0),
    },
    effectiveMonth: Number(meta.effectiveMonth),
    effectiveYear: Number(meta.effectiveYear),
    reason: String(meta.reason ?? '').trim(),
  };
}
